import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Speaker } from 'lucide-react';
import toast from 'react-hot-toast';
import { ensureAnonymousAuth } from '../../services/firebase';
import {
  contactErrorMessage,
  hasSentContact,
  rememberSentContact,
  submitNextEventContact,
} from '../../services/nextEventContact';
import {
  CROWD_SIZE_LABELS,
  CROWD_SIZES,
  DJ_EVENTS_PER_YEAR,
  DJ_EVENTS_PER_YEAR_LABELS,
  EVENT_TIMING_LABELS,
  EVENT_TIMINGS,
  EVENT_TYPE_LABELS,
  EVENT_TYPES,
  MAX_NOTE_LENGTH,
  PREFERRED_CONTACT_LABELS,
  PREFERRED_CONTACTS,
  ROLE_LABELS,
  ROLES,
  detailFields,
  nextStep,
  parseContactSource,
  validateContactForm,
  type ContactStep,
  type CrowdSize,
  type DetailField,
  type DjEventsPerYear,
  type EventTiming,
  type EventType,
  type PreferredContact,
  type Role,
} from '../../utils/nextEventContact';

const OPENING_NOTE =
  "We're still building the full VoteBeats website. Leave your contact info and we'll reach out to talk about what you're looking for.";

interface DetailAnswers {
  eventType: EventType | null;
  eventTiming: EventTiming | null;
  crowdSize: CrowdSize | null;
  djEventsPerYear: DjEventsPerYear | null;
}

const DETAIL_QUESTIONS: Record<DetailField, { question: string; options: readonly string[]; labels: Record<string, string> }> = {
  eventType: { question: 'What kind of event?', options: EVENT_TYPES, labels: EVENT_TYPE_LABELS },
  eventTiming: { question: 'When is it?', options: EVENT_TIMINGS, labels: EVENT_TIMING_LABELS },
  crowdSize: { question: 'About how many people?', options: CROWD_SIZES, labels: CROWD_SIZE_LABELS },
  djEventsPerYear: { question: 'How many events do you DJ a year?', options: DJ_EVENTS_PER_YEAR, labels: DJ_EVENTS_PER_YEAR_LABELS },
};

function optionClass(selected: boolean): string {
  return `px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
    selected
      ? 'border-primary bg-primary/10 text-primary font-medium'
      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary/60'
  }`;
}

// "VoteBeats at your next event?" (ITER-200): a short skippable question flow, then contact info
export function NextEventContactPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('event') || null;
  const source = parseContactSource(searchParams.get('from'));

  const [alreadySent] = useState(() => hasSentContact(eventId));
  const [step, setStep] = useState<ContactStep>(alreadySent ? 'done' : 'role');
  const [authError, setAuthError] = useState<string | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [details, setDetails] = useState<DetailAnswers>({ eventType: null, eventTiming: null, crowdSize: null, djEventsPerYear: null });
  const [note, setNote] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredContact, setPreferredContact] = useState<PreferredContact | null>(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    ensureAnonymousAuth().catch((error) => {
      console.error('Could not connect for the contact form', error);
      setAuthError('Could not connect. Please reload.');
    });
  }, []);

  const handlePickRole = (picked: Role) => {
    setRole(picked);
    setStep(nextStep('role', picked));
  };

  const preferredDisabled = (method: PreferredContact) => (method === 'email' ? !email.trim() : !phone.trim());

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateContactForm({ name, email, phone, preferredContact, note, ageConfirmed });
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    setSending(true);
    try {
      await submitNextEventContact({
        eventId,
        source,
        role,
        ...details,
        note: note.trim() || null,
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        preferredContact,
        ageConfirmed,
      });
      rememberSentContact(eventId);
      setStep('done');
    } catch (sendError) {
      console.error('Could not send the contact form', sendError);
      const message = contactErrorMessage(sendError);
      setFormError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const skipLink = (
    <button type="button" onClick={() => setStep('contact')} className="w-full text-sm text-primary hover:underline">
      Skip, just contact me
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-10 px-4" data-testid="next-event-contact-page">
      <div className="max-w-md mx-auto bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 space-y-5 text-gray-700 dark:text-gray-300">
        <div className="flex items-center gap-3">
          <Speaker className="w-7 h-7 text-primary" />
          <span className="text-lg font-bold text-gray-900 dark:text-white">VoteBeats</span>
        </div>

        {authError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">{authError}</p>
        )}

        {step === 'role' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">VoteBeats at your next event?</h1>
            <p className="text-sm">{OPENING_NOTE}</p>
            <p className="font-medium text-gray-900 dark:text-white">Which best describes you?</p>
            <div className="space-y-2">
              {ROLES.map((option) => (
                <button key={option} type="button" onClick={() => handlePickRole(option)} className={`w-full ${optionClass(false)} py-3`}>
                  {ROLE_LABELS[option]}
                </button>
              ))}
            </div>
            {skipLink}
          </div>
        )}

        {step === 'details' && (
          <div className="space-y-5">
            {detailFields(role).map((field) => {
              const { question, options, labels } = DETAIL_QUESTIONS[field];
              return (
                <fieldset key={field} className="space-y-2" data-testid={`question-${field}`}>
                  <legend className="font-medium text-gray-900 dark:text-white mb-2">{question}</legend>
                  <div className="grid grid-cols-2 gap-2">
                    {options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setDetails((current) => ({ ...current, [field]: current[field] === option ? null : option }))}
                        className={optionClass(details[field] === option)}
                        aria-pressed={details[field] === option}
                      >
                        {labels[option]}
                      </button>
                    ))}
                  </div>
                </fieldset>
              );
            })}
            <button type="button" onClick={() => setStep(nextStep('details', role))} className="btn btn-primary w-full">
              Next
            </button>
            {skipLink}
          </div>
        )}

        {step === 'note' && (
          <div className="space-y-4">
            <label htmlFor="next-event-note" className="block font-medium text-gray-900 dark:text-white">
              Anything else you&rsquo;d like us to know?
            </label>
            <textarea
              id="next-event-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={MAX_NOTE_LENGTH}
              className="input min-h-[100px] resize-none"
            />
            <button type="button" onClick={() => setStep(nextStep('note', role))} className="btn btn-primary w-full">
              Next
            </button>
            {skipLink}
          </div>
        )}

        {step === 'contact' && (
          <form onSubmit={handleSend} className="space-y-4" noValidate>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How can we reach you?</h2>
            <div>
              <label htmlFor="next-event-name" className="block text-sm font-medium mb-1">Name</label>
              <input id="next-event-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoComplete="name" />
            </div>
            <div>
              <label htmlFor="next-event-email" className="block text-sm font-medium mb-1">Email</label>
              <input id="next-event-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} autoComplete="email" />
            </div>
            <div>
              <label htmlFor="next-event-phone" className="block text-sm font-medium mb-1">Phone</label>
              <input id="next-event-phone" type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} autoComplete="tel" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Best way to reach you</p>
              <div className="grid grid-cols-3 gap-2">
                {PREFERRED_CONTACTS.map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setPreferredContact((current) => (current === method ? null : method))}
                    disabled={preferredDisabled(method)}
                    aria-pressed={preferredContact === method}
                    className={`${optionClass(preferredContact === method)} text-center disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {PREFERRED_CONTACT_LABELS[method]}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-1" />
              <span>I&rsquo;m 13 or older, or a parent or guardian is filling this in</span>
            </label>
            {formError && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{formError}</p>
            )}
            <button type="submit" disabled={sending || authError !== null} className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              Send
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="text-center space-y-4" data-testid="next-event-contact-done">
            <CheckCircle2 className="w-12 h-12 mx-auto text-success" />
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {alreadySent ? "You've already sent your info. We'll be in touch soon." : "Thanks! We'll be in touch soon."}
            </p>
            {eventId && window.history.length > 1 && (
              <button type="button" onClick={() => navigate(-1)} className="text-sm text-primary hover:underline">
                Back to the event
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
